import { useEffect } from 'react'
import { useFleet } from './store'
import type { ApiState, GeoPayload, StreamEvent } from '../lib/types'
import { notifyForbidden } from '../lib/notifyForbidden'

/**
 * Bootstrap then stream.
 *
 * The roster comes from /api/state. Kafka Streams' Interactive Queries API used to be the
 * preferred source -- its RocksDB-backed read model was complete immediately after a
 * serving restart, where the in-memory roster (consumed with auto.offset.reset=latest)
 * would otherwise sit empty for minutes until every bus reported again. That engine is
 * retired; serving/app.py's own `_hydrate_vehicles()` now seeds the roster from Postgres's
 * `vehicle_current` at startup, before the Kafka consumer thread starts, so /api/state is
 * complete on restart without it.
 *
 * TIMS logistics overlay (`tims_trips`) is hydrated from /api/state and refreshed on the
 * roster poll + SSE `tims_trips` events. It is keyed by fleet vehicle_id and never mutates
 * Kafka vehicle.meta.
 */

async function getJson<T>(url: string, timeoutMs = 8000, external?: AbortSignal): Promise<T> {
  const ctl = new AbortController()
  const onExternal = () => ctl.abort(external?.reason ?? new Error('aborted'))
  if (external) {
    if (external.aborted) onExternal()
    else external.addEventListener('abort', onExternal, { once: true })
  }
  // A bare ctl.abort() leaves `signal.reason` to the platform's own default --
  // `DOMException: signal is aborted without reason` in Chromium -- which is what
  // every banner built from String(e) showed for a plain timeout, indistinguishable
  // from an actual network failure or a bug. Passing a reason makes fetch's rejection
  // carry it directly, so the banner says what happened: a timeout, not a mystery.
  const timer = setTimeout(() => ctl.abort(new Error(`timed out after ${timeoutMs}ms`)), timeoutMs)
  try {
    const r = await fetch(url, { signal: ctl.signal })
    if (!r.ok) {
      if (r.status === 403) {
        const body = await r.json().catch(() => null) as { error?: string } | null
        notifyForbidden(body?.error ?? `${url} -> 403`)
      }
      throw new Error(`${url} -> ${r.status}`)
    }
    return (await r.json()) as T
  } finally {
    clearTimeout(timer)
    external?.removeEventListener('abort', onExternal)
  }
}

export function useFleetData(): void {
  const applyBootstrap = useFleet((s) => s.applyBootstrap)
  const applyGeo = useFleet((s) => s.applyGeo)
  const applyEvent = useFleet((s) => s.applyEvent)
  const applyStats = useFleet((s) => s.applyStats)
  const refreshCells = useFleet((s) => s.refreshCells)
  const setConn = useFleet((s) => s.setConn)
  const addBanner = useFleet((s) => s.addBanner)

  // ------------------------------------------------------------------- server capability
  const applyConfig = useFleet((s) => s.applyConfig)
  useEffect(() => {
    let alive = true
    getJson<{ photoreal: boolean; maps_key: string }>('/api/config')
      .then((c) => { if (alive) applyConfig(c) })
      .catch(() => { /* no config endpoint: photoreal simply stays unavailable */ })
    return () => { alive = false }
  }, [applyConfig])

  // ------------------------------------------------------------------ reference geometry
  // Polled, not fetch-once: geofence names/shapes are edited through the admin API while
  // the console is already open (GeofencesSection.tsx), and /api/geo has no SSE event of
  // its own to ride in on. A fetch-once snapshot meant an admin's correction was only ever
  // visible to a tab opened (or hard-reloaded) after the edit -- every tab that was already
  // open kept rendering the pre-edit name/shape indefinitely. 60s, not the roster's 8s:
  // geometry changes by admin action, not by the second.
  useEffect(() => {
    let alive = true
    let inflight: AbortController | null = null
    const tick = () => {
      // A backgrounded tab has no one watching the map -- a stale geofence shape for a
      // few extra minutes until the tab is reopened costs nothing, but firing this on
      // every inactive tab someone forgot open does cost the server. Skip the fetch
      // entirely rather than let it fire into the void.
      if (document.visibilityState === 'hidden') return
      // The previous attempt is obsolete the moment a new one starts. Leaving it
      // running let its 8s abort land later and paint an error over a map that had
      // already loaded. /api/geo's own query is tens of milliseconds (31 fences, 4
      // routes); the abort was the shared 8s roster budget firing while this response
      // was still queued behind the other polls. 30s is enough for that queue.
      inflight?.abort()
      const ctl = new AbortController()
      inflight = ctl
      getJson<GeoPayload>('/api/geo', 30_000, ctl.signal)
        .then((g) => {
          if (!alive || ctl.signal.aborted) return
          applyGeo(g)
          if (!g.tiles) {
            addBanner({
              id: 'no-tiles',
              tone: 'warn',
              message: 'Basemap not installed — run `make tiles`. Geometry still renders.',
            })
          }
          if (!g.counts.geofences) {
            addBanner({
              id: 'no-geo',
              tone: 'warn',
              message: 'No geofences on disk — run `make geofences` to parse the Earth project.',
            })
          }
        })
        .catch(() => {
          // A failed or superseded fetch keeps the last good geometry. This is
          // reference data, not a live fault, and the banner was the thing operators
          // were seeing ("timed out after 8000ms") while the map itself was fine.
        })
    }
    tick()
    const id = setInterval(tick, 60000)
    // Catches the tab back up the moment it's reactivated, instead of leaving it showing
    // whatever was current up to 60s before the person tabbed away.
    const onVisible = () => { if (document.visibilityState === 'visible') tick() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      alive = false
      inflight?.abort()
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [applyGeo, addBanner])

  // ------------------------------------------------------------------------- roster + SSE
  useEffect(() => {
    let alive = true
    let es: EventSource | null = null

    async function bootstrap(): Promise<void> {
      // /api/state carries the roster plus alerts, KPIs, quality and stats.
      const state = await getJson<ApiState>('/api/state').catch(() => null)
      if (!alive) return
      if (state) {
        applyBootstrap(state)
      } else {
        addBanner({
          id: 'no-backend',
          tone: 'error',
          message: 'The serving read model is unreachable.',
        })
      }
    }

    // DEBOUNCED, not immediate. Confirmed live: /events reconnects every ~13-16s even
    // under completely healthy conditions -- the connection closes on its own well
    // before this handler's 15s server-side keepalive ever fires, EventSource's built-in
    // retry reopens it 2-3s later, and onopen fires again. That cycle is silent and
    // harmless (the roster-sync poll below refreshes independently every 8s regardless),
    // but flipping `conn` to 'reconnecting' on every onerror turned it into a console
    // whose header badge visibly flickers LIVE/OFFLINE on a loop -- reported live as
    // "why does it show offline when it's online", same shape of question the roster-merge
    // fix (see mergeRoster's own comment) already answered once for the fleet roster
    // itself. A brief blip that self-heals within a couple of retry cycles should never
    // reach the operator; only a connection that stays down is a real thing to show.
    let downTimer: ReturnType<typeof setTimeout> | null = null

    function connect(): void {
      es = new EventSource('/events')
      es.onopen = () => {
        if (downTimer) { clearTimeout(downTimer); downTimer = null }
        setConn('live')
        useFleet.getState().dismissBanner('sse-down')
      }
      es.onerror = () => {
        // EventSource reconnects on its own; re-bootstrap on the next open to close the
        // gap. Only surface 'reconnecting' if onopen hasn't already cancelled this within
        // the grace window -- comfortably past the observed 2-3s ordinary retry delay.
        if (!downTimer) {
          downTimer = setTimeout(() => { setConn('reconnecting'); downTimer = null }, 5000)
        }
      }
      es.onmessage = (ev: MessageEvent<string>) => {
        try {
          applyEvent(JSON.parse(ev.data) as StreamEvent)
        } catch {
          /* a malformed frame must not kill the stream */
        }
      }
    }

    void bootstrap().then(connect)
    return () => {
      alive = false
      es?.close()
      if (downTimer) clearTimeout(downTimer)
    }
  }, [applyBootstrap, applyEvent, setConn, addBanner])

  // ------------------------------------------------------- roster sync (heal ghost plates)
  // SSE only ever *adds* vehicles. After a server-side prune the browser would keep the
  // ghosts until a hard refresh. Replacing the roster from /api/state every few seconds
  // makes the TV wall match the live read model without asking the operator to reload.
  //
  // cells are re-synced here too, for the same "self-heal without a reload" reason, but a
  // more pressing one: /events drops messages on every one of its routine ~13-16s
  // reconnects (see connect()'s own comment above) with no replay, and a cell only emits
  // once a minute -- so a single dropped SSE frame can leave a visibly wrong congestion
  // score on screen with nothing to correct it until this poll catches up. Reported live
  // as "it's not live, needs a refresh every time" -- a refresh "fixed" it only because it
  // re-bootstraps from this same authoritative snapshot, bypassing the lossy SSE channel
  // entirely. This poll does the same thing automatically.
  //
  // triage rides along for a second, unrelated reason on top of the dropped-SSE-frame one
  // above: `_publish`'s fan-out is in-process only, and this deployment runs 2 pods with
  // no session affinity, so an ack processed by one pod is never broadcast to a client
  // connected to the other. /api/state's own `triage` is Postgres-backed specifically so
  // this poll is authoritative regardless of which pod answers it (serving/app.py's
  // _triage_snapshot_from_db) -- without this, a colleague's ack could stay invisible on
  // this tab indefinitely, not just for one reconnect cycle.
  useEffect(() => {
    const tick = () => {
      // This is the single heaviest, highest-frequency request the whole console makes
      // (the full roster + alerts + KPIs + stats snapshot, every 8s). A tab nobody is
      // looking at has no self-healing to do -- SSE's dropped-reconnect frames and the
      // cross-pod triage gap this poll exists to paper over (see the block comment
      // above) only matter to a screen someone is actually watching. Confirmed live:
      // this was firing at full cadence on backgrounded tabs with no pause at all.
      if (document.visibilityState === 'hidden') return
      void getJson<ApiState>('/api/state')
        .then((s) => {
          applyEvent({ type: 'roster', data: { vehicles: s.vehicles ?? [] } })
          if (s.tims_trips) applyEvent({ type: 'tims_trips', data: s.tims_trips })
          if (s.live_counts) applyEvent({ type: 'live_counts', data: s.live_counts })
          if (s.triage) applyEvent({ type: 'triage_sync', data: s.triage })
          // Same cross-pod SSE gap as triage above: mark_breakdown's _publish_live_ops()
          // broadcast only reaches a browser pinned to the same pod that handled the
          // POST, so a breakdown marked from the Actionables list on one pod never
          // updated that list's own "down" state on a tab pinned to the other pod until
          // a hard reload re-bootstrapped from this same /api/state snapshot. This poll
          // already fetches s.actionables every 8s; it just never applied it.
          if (s.actionables) applyEvent({ type: 'actionables', data: { ts: Date.now(), items: s.actionables } })
          refreshCells(s.cells)
          applyStats(s.stats, s.quality ?? {})
        })
        .catch(() => { /* bootstrap banner already covers backend loss */ })
    }
    tick()
    const id = setInterval(tick, 8000)
    // Re-syncs immediately on return instead of leaving the tab stale for up to 8s --
    // short enough normally to not matter, but a tab backgrounded for an hour would
    // otherwise show an hour-stale roster for up to 8 more seconds after refocus.
    const onVisible = () => { if (document.visibilityState === 'visible') tick() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible) }
  }, [applyEvent, applyStats, refreshCells])
}
